import styled from 'styled-components'

import { ColorSwatch } from '@percent/lemonade'

import { LegendItem as LegendItemModel } from '../../utils/metricView'

// Swatch + label legend shared by every insights chart, so bar and donut cards
// read with one visual language. The swatch itself is the Lemonade `ColorSwatch`
// design-system primitive; this component only owns the insights-specific
// row layout. Swatch dimensions match the design (Figma node 5676:1946).
const SWATCH_SIZE_PX = 10
const SWATCH_RADIUS_PX = 2

const LegendRow = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.sizes[3]} ${theme.sizes.s};
    padding-top: ${theme.sizes.s};
  `}
`

// A muted entry names a series the chart couldn't plot (its unit had no axis), so
// it reads as present-but-not-drawn rather than disappearing from the card.
const MUTED_SWATCH_OPACITY = 0.4

const LegendLabel = styled.span<{ $muted: boolean }>`
  ${({ theme, $muted }) => `
    display: inline-flex;
    align-items: center;
    gap: ${theme.sizes.xs};
    font-size: ${theme.fontSizes.caption};
    font-weight: ${theme.fontWeights.medium};
    line-height: 1rem;
    color: ${
      $muted ? theme.semantic.contentTertiary : theme.semantic.contentSecondary
    };
    ${$muted ? `font-style: italic;` : ''}
  `}
`

// Takes the whole row so the entries wrap beneath it rather than beside it — the
// caption belongs to every entry, not to the first one.
const LegendHeading = styled.span`
  ${({ theme }) => `
    flex: 0 0 100%;
    font-size: ${theme.fontSizes.caption};
    font-weight: ${theme.fontWeights.semiBold};
    line-height: 1rem;
    color: ${theme.semantic.contentTertiary};
  `}
`

const Swatch = styled.span<{ $muted: boolean }>`
  display: inline-flex;
  ${({ $muted }) => ($muted ? `opacity: ${MUTED_SWATCH_OPACITY};` : '')}
`

export type InsightsLegendProps = {
  items: LegendItemModel[]
  /**
   * Pre-translated caption naming what the entries are — the breakdown's row axis
   * ("Country") on a legend that enumerates rows. Omitted where the entries are
   * series: the chart's own axes already name those.
   */
  heading?: string
  testId?: string
}

export function InsightsLegend({
  items,
  heading,
  testId,
}: Readonly<InsightsLegendProps>) {
  return (
    <LegendRow data-testid={testId}>
      {heading ? <LegendHeading>{heading}</LegendHeading> : null}
      {items.map((item) => (
        <LegendLabel
          key={item.key}
          $muted={Boolean(item.muted)}
          title={item.tooltip}
        >
          <Swatch $muted={Boolean(item.muted)}>
            <ColorSwatch
              color={item.color}
              size={SWATCH_SIZE_PX}
              radius={SWATCH_RADIUS_PX}
            />
          </Swatch>
          {item.label}
        </LegendLabel>
      ))}
    </LegendRow>
  )
}
